import { useTranslation } from 'react-i18next'
import { SectionHeader, EmptyOK } from './primitives'
import { ChangeCard } from './ChangeCard'
import { useImmeubleReliability } from './useReliability'
import { AssetProperties } from '../AssetProperties'

/**
 * Building-level data surface. Reconciliation cards (items needing arbitration)
 * sit on top; the full editable property surface — every confirmed value, with
 * inline edit, history, add/delete — is embedded below via AssetProperties.
 * Pending candidates are surfaced as ChangeCards here.
 */
export function ImmeubleSection({ org, assetUid, locale }: { org: string; assetUid: string; locale: string }) {
  const { t } = useTranslation('data-reliability')
  const { changes, isLoading, processing, acceptCandidate, keepCurrent } = useImmeubleReliability(org, assetUid, locale)

  return (
    <div data-testid="dr-section-immeuble">
      <SectionHeader title={t('immeuble.title')} desc={t('immeuble.desc')} />

      {isLoading ? (
        <div className="space-y-3">
          <div className="h-32 animate-pulse rounded-xl border border-border bg-muted/30" />
        </div>
      ) : (
        <>
          {changes.length === 0 ? (
            <EmptyOK label={t('immeuble.all_reliable')} />
          ) : (
            changes.map((change) => (
              <ChangeCard
                key={change.propertyUid}
                org={org}
                change={change}
                disabled={processing}
                onAccept={(uid) => acceptCandidate(change, uid)}
                onKeepCurrent={() => keepCurrent(change)}
              />
            ))
          )}

          {/* Confirmed values — the "Validé automatiquement" fold from the
              prototype. Still fully editable per property (inline edit, add,
              history, delete) via AssetProperties' reliability variant. */}
          <div className="mt-4" data-testid="dr-immeuble-properties">
            <AssetProperties org={org} assetUid={assetUid} variant="reliability" />
          </div>
        </>
      )}
    </div>
  )
}
